import React, { useState, useMemo, memo } from 'react';
import PropTypes from 'prop-types';

const NeumorphicInput = ({ type, id, name, className, placeholder, value, onChange, onKeyDown, required, disabled }) => {
  // Generate unique IDs only once per component instance (memoized to prevent re-generation on every render)
  // CRITICAL OPTIMIZATION: These IDs were previously generated on every render using Math.random(),
  // causing 6 new SVG filter IDs per render. Now memoized to generate only once.
  const filterIds = useMemo(() => {
    const uniqueId = id || Math.random().toString(36).substr(2, 9);
    return {
      filterId: `filter_${uniqueId}`,
      focusFilterId: `focus_filter_${uniqueId}`,
      hoverGradient1Id: `hoverGradient1_${uniqueId}`,
      hoverGradient2Id: `hoverGradient2_${uniqueId}`,
      hoverGradient3Id: `hoverGradient3_${uniqueId}`,
      hoverGradient4Id: `hoverGradient4_${uniqueId}`
    };
  }, [id]);

  const { filterId, focusFilterId, hoverGradient1Id, hoverGradient2Id, hoverGradient3Id, hoverGradient4Id } = filterIds;

  // Add states to track focus and hover
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className="login-form-input-container" 
      style={{ position: 'relative', width: '100%', height: '54px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* CSS Animation for smooth gradient blending */}
      <style>
        {`
          @keyframes gradientBlend1 {
            0%, 100% { opacity: 0.8; }
            25%, 75% { opacity: 0; }
            50% { opacity: 0.3; }
          }
          
          @keyframes gradientBlend2 {
            0%, 50%, 100% { opacity: 0.3; }
            25% { opacity: 0.8; }
            75% { opacity: 0; }
          }
          
          @keyframes gradientBlend3 {
            0%, 100% { opacity: 0; }
            25% { opacity: 0.3; }
            50% { opacity: 0.8; }
            75% { opacity: 0.3; }
          }
          
          @keyframes gradientBlend4 {
            0% { opacity: 0.3; }
            25% { opacity: 0; }
            50%, 100% { opacity: 0.3; }
            75% { opacity: 0.8; }
          }
          
          .gradient-1 {
            animation: gradientBlend1 3s infinite ease-in-out;
          }
          
          .gradient-2 {
            animation: gradientBlend2 3s infinite ease-in-out;
          }
          
          .gradient-3 {
            animation: gradientBlend3 3s infinite ease-in-out;
          }
          
          .gradient-4 {
            animation: gradientBlend4 3s infinite ease-in-out;
          }
          
          /* Add placeholder color styling */
          ::placeholder {
            color: #9C9DA7;
            font-size: clamp(10px, 1.6vw, 13px);
            opacity: 1;
          }

          :-ms-input-placeholder {
            font-size: clamp(10px, 1.6vw, 13px);
            color: #9C9DA7;
          }

          ::-ms-input-placeholder {
            font-size: clamp(10px, 1.6vw, 13px);
            color: #9C9DA7;
          }
        `}
      </style>
    
      {/* Hover effect SVGs - only shown when hovered but not focused */}
      {isHovered && !isFocused && (
        <>
          <svg 
            style={{ 
              position: 'absolute', 
              width: 'calc(100% + 6px)', 
              height: 'calc(80% + 6px)', 
              top: '-3px', 
              left: '-3px',
              zIndex: 0,
              opacity: 0
            }}
            className="gradient-1"
            viewBox="0 0 460 52" 
            fill="none"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M13.1362 48.4884L2.39527 29.6917C2.13625 29.2384 2 28.7254 2 28.2033V5C2 3.34315 3.34316 2 5.00001 2H444.259C445.336 2 446.33 2.57686 446.864 3.51158L457.605 22.3083C457.864 22.7616 458 23.2746 458 23.7967V47C458 48.6569 456.657 50 455 50H15.741C14.6644 50 13.6704 49.4231 13.1362 48.4884Z" stroke={`url(#${hoverGradient1Id})`} strokeWidth="2.5"/>
            <defs>
              <linearGradient id={hoverGradient1Id} x1="222" y1="50" x2="222" y2="7.5" gradientUnits="userSpaceOnUse">
                <stop stopColor="#C000FF"/>
                <stop offset="0.02" stopColor="#3C014F"/>
                <stop offset="0.495" stopColor="#3C014F"/>
                <stop offset="0.95" stopColor="#3C014F"/>
                <stop offset="0.99" stopColor="#C000FF"/>
              </linearGradient>
            </defs>
          </svg>
          
          <svg 
            style={{ 
              position: 'absolute', 
              width: 'calc(100% + 6px)', 
              height: 'calc(80% + 8px)', 
              top: '-3px', 
              left: '-3px',
              zIndex: 0,
              opacity: 0
            }}
            className="gradient-2"
            viewBox="0 0 460 52" 
            fill="none"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M13.1362 48.4884L2.39527 29.6917C2.13625 29.2384 2 28.7254 2 28.2033V5C2 3.34315 3.34316 2 5.00001 2H444.259C445.336 2 446.33 2.57686 446.864 3.51158L457.605 22.3083C457.864 22.7616 458 23.2746 458 23.7967V47C458 48.6569 456.657 50 455 50H15.741C14.6644 50 13.6704 49.4231 13.1362 48.4884Z" stroke={`url(#${hoverGradient2Id})`} strokeWidth="2.5"/>
            <defs>
              <linearGradient id={hoverGradient2Id} x1="458" y1="26" x2="9" y2="26" gradientUnits="userSpaceOnUse">
                <stop stopColor="#C000FF"/>
                <stop offset="0.02" stopColor="#3C014F"/>
                <stop offset="0.495" stopColor="#3C014F"/>
                <stop offset="0.95" stopColor="#3C014F"/>
                <stop offset="0.99" stopColor="#C000FF"/>
              </linearGradient>
            </defs>
          </svg>
          
          <svg 
            style={{ 
              position: 'absolute', 
              width: 'calc(100% + 6px)', 
              height: 'calc(80% + 6px)', 
              top: '-3px', 
              left: '-3px',
              zIndex: 0,
              opacity: 0
            }}
            className="gradient-3"
            viewBox="0 0 460 52" 
            fill="none"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M13.1362 48.4884L2.39527 29.6917C2.13625 29.2384 2 28.7254 2 28.2033V5C2 3.34315 3.34316 2 5.00001 2H444.259C445.336 2 446.33 2.57686 446.864 3.51158L457.605 22.3083C457.864 22.7616 458 23.2746 458 23.7967V47C458 48.6569 456.657 50 455 50H15.741C14.6644 50 13.6704 49.4231 13.1362 48.4884Z" stroke={`url(#${hoverGradient3Id})`} strokeWidth="2.5"/>
            <defs>
              <linearGradient id={hoverGradient3Id} x1="2" y1="26" x2="458" y2="26" gradientUnits="userSpaceOnUse">
                <stop stopColor="#C000FF"/>
                <stop offset="0.02" stopColor="#3C014F"/>
                <stop offset="0.495" stopColor="#3C014F"/>
                <stop offset="0.95" stopColor="#3C014F"/>
                <stop offset="0.99" stopColor="#C000FF"/>
              </linearGradient>
            </defs>
          </svg>
          
          <svg 
            style={{ 
              position: 'absolute', 
              width: 'calc(100% + 6px)', 
              height: 'calc(80% + 6px)', 
              top: '-3px', 
              left: '-3px',
              zIndex: 0,
              opacity: 0
            }}
            className="gradient-4"
            viewBox="0 0 460 52" 
            fill="none"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M13.1362 48.4884L2.39527 29.6917C2.13625 29.2384 2 28.7254 2 28.2033V5C2 3.34315 3.34316 2 5.00001 2H444.259C445.336 2 446.33 2.57686 446.864 3.51158L457.605 22.3083C457.864 22.7616 458 23.2746 458 23.7967V47C458 48.6569 456.657 50 455 50H15.741C14.6644 50 13.6704 49.4231 13.1362 48.4884Z" stroke={`url(#${hoverGradient4Id})`} strokeWidth="2.5"/>
            <defs>
              <linearGradient id={hoverGradient4Id} x1="230" y1="2" x2="230" y2="50" gradientUnits="userSpaceOnUse">
                <stop stopColor="#C000FF"/>
                <stop offset="0.15" stopColor="#3C014F"/>
                <stop offset="0.495" stopColor="#3C014F"/>
                <stop offset="0.9899" stopColor="#3C014F"/>
                <stop offset="0.99" stopColor="#C000FF"/>
              </linearGradient>
            </defs>
          </svg>
        </>
      )}
        
      {/* Focus outline SVG that appears only when focused */}
      {isFocused && (
        <svg 
          style={{ 
            position: 'absolute', 
            width: 'calc(100% + 19px)',
            height: 'calc(80% + 20px)',
            top: '-9.7px', 
            left: '-9.9px',
            zIndex: 0
          }}
          viewBox="0 0 486 78"
          fill="none"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <filter id={focusFilterId} x="-12" y="-12" width="100" height="12" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
              <feOffset/>
              <feGaussianBlur stdDeviation="4"/>
              <feComposite in2="hardAlpha" operator="over"/> 
              <feColorMatrix type="matrix" values="0 0 0 0 0.957963 0 0 0 0 0.829167 0 0 0 0 1 0 0 0 1 0"/>
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
            </filter>
          </defs>
          
          {/* Add a separate glow path with no dark edge */}
          <path 
            d="M24.1362 63.4884L13.3953 44.6917C13.1362 44.2384 13 43.7254 13 43.2033V16C13 14.3431 14.3432 13 16 13H459.259C460.336 13 461.33 13.5769 461.864 14.5116L472.605 33.3083C472.864 33.7616 473 34.2746 473 34.7967V62C473 63.6569 471.657 65 470 65H26.741C25.6644 65 24.6704 64.4231 24.1362 63.4884Z" 
            stroke="#F4D3FF"
            strokeWidth="2"
            fill="none"
            filter="blur(5px)"
          />
          
          {/* Main border with no filter */}
          <path 
            d="M24.1362 63.4884L13.3953 44.6917C13.1362 44.2384 13 43.7254 13 43.2033V16C13 14.3431 14.3432 13 16 13H459.259C460.336 13 461.33 13.5769 461.864 14.5116L472.605 33.3083C472.864 33.7616 473 34.2746 473 34.7967V62C473 63.6569 471.657 65 470 65H26.741C25.6644 65 24.6704 64.4231 24.1362 63.4884Z" 
            stroke="#9404C3" 
            strokeWidth="3"
            fill="none"
          />
        </svg>
      )}
      
      {/* Main SVG background */}
      <svg 
        style={{ position: 'absolute', width: '100%', height: '80%', top: 0, left: 0, zIndex: 1 }}
        viewBox="0 0 462 54" 
        fill="none"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g filter={`url(#${filterId})`}>
          <path d="M14.1362 49.4884L3.39527 30.6917C3.13625 30.2384 3 29.7254 3 29.2033V6C3 4.34315 4.34316 3 6.00001 3H445.259C446.336 3 447.33 3.57686 447.864 4.51158L458.605 23.3083C458.864 23.7616 459 24.2746 459 24.7967V48C459 49.6569 457.657 51 456 51H16.741C15.6644 51 14.6704 50.4231 14.1362 49.4884Z" fill="#282B32"/>
        </g>
        <defs>
          <filter id={filterId} x="-7" y="-7" width="476" height="68" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-1" dy="-1"/>
            <feGaussianBlur stdDeviation="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.137255 0 0 0 0.5 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_34_64"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="1" dy="1"/>
            <feGaussianBlur stdDeviation="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.203922 0 0 0 0 0.219608 0 0 0 0 0.254902 0 0 0 0.3 0"/>
            <feBlend mode="normal" in2="effect1_dropShadow_34_64" result="effect2_dropShadow_34_64"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_34_64" result="shape"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="10" dy="10"/>
            <feGaussianBlur stdDeviation="12.5"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.137255 0 0 0 0.9 0"/>
            <feBlend mode="normal" in2="shape" result="effect3_innerShadow_34_64"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-10" dy="-10"/>
            <feGaussianBlur stdDeviation="10"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.203922 0 0 0 0 0.219608 0 0 0 0 0.254902 0 0 0 0.9 0"/>
            <feBlend mode="normal" in2="effect3_innerShadow_34_64" result="effect4_innerShadow_34_64"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="10" dy="-10"/>
            <feGaussianBlur stdDeviation="10"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.137255 0 0 0 0.2 0"/>
            <feBlend mode="normal" in2="effect4_innerShadow_34_64" result="effect5_innerShadow_34_64"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-10" dy="10"/>
            <feGaussianBlur stdDeviation="10"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.137255 0 0 0 0.2 0"/>
            <feBlend mode="normal" in2="effect5_innerShadow_34_64" result="effect6_innerShadow_34_64"/>
          </filter>
        </defs>
      </svg>
      
      {/* Actual input field */}
      <input
        type={type}
        id={id}
        name={name}
        className={`${className} login-form-input-overlay`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        required={required}
        disabled={disabled}
        style={{
          position: 'absolute',
          width: '90%',
          height: '80%',
          padding: '0 1.2rem',
          backgroundColor: 'transparent',
          color: 'white',
          fontSize: 'clamp(10px, 1.8vw, 14px)',
          fontFamily: 'Poppins',
          zIndex: 2,
          border: 'none',
          outline: 'none',
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
    </div>
  );
};

// Custom comparison function for React.memo
// Only re-render if props that affect the visual output change
const arePropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.type === nextProps.type &&
    prevProps.value === nextProps.value &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.id === nextProps.id &&
    prevProps.name === nextProps.name &&
    prevProps.className === nextProps.className &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.onKeyDown === nextProps.onKeyDown &&
    prevProps.required === nextProps.required
  );
};

NeumorphicInput.propTypes = {
  type: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool
};

NeumorphicInput.defaultProps = {
  type: 'text',
  className: '',
  placeholder: '',
  value: '',
  required: false,
  disabled: false
};

NeumorphicInput.displayName = 'NeumorphicInput';

export default memo(NeumorphicInput, arePropsEqual);